import { useDispatch } from 'react-redux'

import * as Staking from '../contracts/staking'

import {
  setLockLevel,
  setStakedAmount,
  setTier,
  setTierReference,
  setTimeUnlock,
} from '../modules/staking/actions'

import { amountFromWei, amountToWei } from '../utils/amount'
import { STAKED_DECIMALS } from '../page_components/Staking'
import { Big, BigSource } from 'big.js'
import { TierReference } from '../types/reducerTypes'

import useLPTokenContract from './useLPTokenContract'
import { isWithinAllowance, MAX_ALLOWANCE } from './useMasterContract'
import useMultiStepTx from './useMultiStepTx'
import { getGasFeesPolygon } from '../api'
import useNetworkId from './useNetworkId'
import { useEffect, useState } from 'react'
import { ethers } from 'ethers'

export interface IStakingContract {
  deposit: (
    lockInPeriod: number,
    amount: BigSource,
    tax: number,
  ) => Promise<void>
  getTier: () => Promise<string>
  getUserState: () => Promise<void>
  withdraw: (amount: BigSource, tax: number) => Promise<void>
  withdrawAll: () => Promise<void>
  getAllTierInfo: () => void
  transferReflection: (amount: BigSource) => Promise<void>
  getReflection: () => Promise<number>
}

export const lockInPeriods = [0, 30, 60, 90]

export const allTierTitles = [
  'Backer',
  'Starter',
  'Investor',
  'Strategist',
  'Venturist',
  'Evangelist',
  'Evangelist Pro',
]

const initContract = (
  provider: ethers.BrowserProvider,
  account: string | undefined,
) => {
  if (provider) {
    const contract = new ethers.Contract(
      Staking.address,
      Staking.abi as ethers.InterfaceAbi,
      provider,
    )
    return contract
  }
  return null
}

const useStakingContract = (
  provider: ethers.BrowserProvider,
  account: string | undefined,
): IStakingContract => {
  const dispatch = useDispatch()
  const [contract, setContract] = useState<any>(null)
  const lptContract = useLPTokenContract(provider, account)
  const { setTotalSteps, nextStepCallback } = useMultiStepTx()

  const { wrongNetwork } = useNetworkId()

  useEffect(() => {
    if (!provider || !account) {
      setContract(null)
      return
    }
    setContract(initContract(provider, account))
  }, [provider, account])

  async function deposit(lockInPeriod: number, amount: BigSource, tax: number) {
    if (wrongNetwork) return
    try {
      if (!account) throw new Error('Account not provided')

      const toSend = amountToWei(amount, STAKED_DECIMALS).toString()
      const taxValue = amountToWei(new Big(tax).times(1.05), 18)

      const allowance = await lptContract?.allowance(account, Staking.address)
      if (!isWithinAllowance(allowance, toSend)) {
        setTotalSteps(2)
        await lptContract.increaseAllowance(Staking.address, MAX_ALLOWANCE)
      } else {
        setTotalSteps(1)
      }

      const gas = await contract?.stake.estimateGas(lockInPeriod, toSend, {
        from: account,
        value: taxValue,
      })

      const { maxFeePerGas, maxPriorityFeePerGas } = await getGasFeesPolygon()

      return await contract?.stake(lockInPeriod, toSend).send(
        {
          value: taxValue,
          gas,
          maxFeePerGas,
          maxPriorityFeePerGas,
          from: account,
        },
        nextStepCallback,
      )
    } catch (err) {
      console.error('Error in deposit function:', err)
      throw err // Or handle the error as needed
    }
  }

  async function getTier() {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      const tier = await contract.getTierOf(account)
      dispatch(setTier(Number(tier)))
      return tier
    } catch (err) {
      console.error('Error in getTier function:', err)
      return null // Or handle the error as needed
    }
  }

  async function getReflection() {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      return await contract.getReflection()
    } catch (err) {
      console.error('Error in getReflection function:', err)
      return 0 // Return a default or handle differently
    }
  }

  async function getAllocation(level: number, tier: number) {
    try {
      if (wrongNetwork) throw new Error('Wrong network')
      if (!contract) throw new Error('Contract not initialized')
      return await contract.getAllocations(level, tier)
    } catch (err) {
      console.error('Error in getAllocation function:', err)
      return null // Return a default or handle differently
    }
  }

  async function getDeposit(level: number, tier: number) {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      return await contract.getDeposits(level, tier)
    } catch (err) {
      console.error('Error in getDeposit function:', err)
      return null // Return a default or handle differently
    }
  }

  async function getAllTierInfo() {
    try {
      if (wrongNetwork) throw new Error('Wrong network')
      if (!contract) throw new Error('Contract not initialized')
      const tiers: TierReference = {}
      for (let i = 0; i < lockInPeriods.length; i++) {
        const period = lockInPeriods[i]
        tiers[period] = {}
        for (let j = 0; j < allTierTitles.length; j++) {
          const tier = allTierTitles[j]
          const allocation = await getAllocation(i + 1, j + 1)
          const required = await getDeposit(i + 1, j + 1)
          if (j !== 6 || (j === 6 && i === 3))
            tiers[period][tier] = { allocation, required }
        }
      }

      dispatch(setTierReference(tiers))
    } catch (err) {
      console.error('Error in getAllTierInfo function:', err)
    }
  }

  async function getUserState() {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      const userState = await contract.getUserState(account)
      if (userState?.[1])
        dispatch(setLockLevel(userState[1] === '0' ? '1' : userState[1]))
      if (userState?.[2]) {
        dispatch(
          setStakedAmount(
            new Big(amountFromWei(userState?.[2], STAKED_DECIMALS).toFixed(1)),
          ),
        )
      }
      if (userState?.[3]) dispatch(setTimeUnlock(Number(userState[3])))
    } catch (err) {
      console.error('Error in getUserState function:', err)
    }
  }

  async function withdraw(amount: BigSource) {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      const toSend = amountToWei(amount, STAKED_DECIMALS).toString()
      const gas = await contract.unstake(toSend).estimateGas()
      await contract.unstake(toSend).send({ gas })
    } catch (err) {
      console.error('Error in withdraw function:', err)
      throw err
    }
  }

  async function withdrawAll() {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      const gas = await contract.unstake().estimateGas()
      return await contract.unstake({ gasLimit: gas })
    } catch (err) {
      console.error('Error in withdrawAll function:', err)
      throw err
    }
  }

  async function transferReflection(amount: BigSource) {
    if (wrongNetwork) return
    try {
      if (!contract) throw new Error('Contract not initialized')
      const gas = await contract.transferReflection(amount).estimateGas()
      return await contract.transferReflection(amount, { gasLimit: gas })
    } catch (err) {
      console.error('Error in transferReflection function:', err)
      throw err
    }
  }

  return {
    deposit,
    getTier,
    withdraw,
    withdrawAll,
    getUserState,
    getAllTierInfo,
    transferReflection,
    getReflection,
  }
}

export default useStakingContract
